import { Box } from '@mui/material';
import { EventSession } from '@types';
import React, { useEffect, useMemo, useState } from 'react';
import TicketsByEventTitle from './TicketsByEventTitle';
import { Typography, IconButton, Icon } from '@elements';
import EventTicket from '@components/event-ticket';
import { Link, useSearchParams } from 'react-router-dom';
import { useEventSessionsPage } from '@hooks/useEventSessions';
import PrevIcon from '@images/prev.svg';
import NextIcon from '@images/next.svg';
import Loader from '@components/loader/Loader';

const PAGE_SIZE = 20;

const normalizeEventSession = (event: EventSession): EventSession => ({
  ...event,
  seats: event.seats ?? [],
  eventSessionTypeName: event.eventSessionTypeName ?? event.eventTypeName,
});

const TicketsByEvent = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const { data, isFetching } = useEventSessionsPage('TICKET', pageNumber, PAGE_SIZE);
  const content = data?.content ?? [];

  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';

  useEffect(() => {
    setPageNumber(0);
  }, [filter]);

  const eventTypes = useMemo(() => {
    const types = new Set<string>();
    content.forEach((eventSession) => {
      if (eventSession.eventTypeName) types.add(eventSession.eventTypeName);
    });
    return Array.from(types);
  }, [content]);

  const filteredContent = useMemo(() => {
    if (filter === 'all') return content;
    return content.filter((eventSession) => eventSession.eventTypeName === filter);
  }, [content, filter]);

  const seatsCount = useMemo(
    () => filteredContent.reduce((acc, es) => acc + (es.seats ?? []).length, 0),
    [filteredContent]
  );

  const hasNextPage = (data?.totalPages ?? 0) > 0 && pageNumber < (data?.totalPages ?? 1) - 1;
  const hasPrevPage = pageNumber > 0;

  const handleNextPage = () => {
    if (hasNextPage) setPageNumber((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (hasPrevPage) setPageNumber((p) => p - 1);
  };

  if (isFetching && content.length === 0) {
    return <Loader isShowLoader />;
  }

  if (!data || filteredContent.length === 0) {
    return (
      <Box>
        <TicketsByEventTitle count={0} seatsCount={0} eventTypes={eventTypes} />
        <Typography>Ничего не найдено</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '414px' }}>
      <TicketsByEventTitle
        count={filteredContent.length}
        seatsCount={seatsCount}
        eventTypes={eventTypes}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
        {filteredContent.map((eventSession) => (
          <Link
            style={{ textDecoration: 'none' }}
            to={`${eventSession.id}`}
            key={eventSession.id}
          >
            <EventTicket event={normalizeEventSession(eventSession)} />
          </Link>
        ))}
      </Box>
      {data.totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            mt: '24px',
            mb: '24px',
          }}
        >
          <IconButton
            onClick={handlePrevPage}
            disabled={!hasPrevPage}
            sx={{
              opacity: hasPrevPage ? 1 : 0.5,
              cursor: hasPrevPage ? 'pointer' : 'not-allowed',
            }}
          >
            <Icon src={PrevIcon} width={24} height={24} />
          </IconButton>
          <Typography>
            {pageNumber + 1} / {data.totalPages}
          </Typography>
          <IconButton
            onClick={handleNextPage}
            disabled={!hasNextPage}
            sx={{
              opacity: hasNextPage ? 1 : 0.5,
              cursor: hasNextPage ? 'pointer' : 'not-allowed',
            }}
          >
            <Icon src={NextIcon} width={24} height={24} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default TicketsByEvent;
